import { PropsWithChildren } from 'react';

import { makeStyles } from '@mui/styles';

import AccountTreeIcon from '@mui/icons-material/AccountTree';
import AnnouncementIcon from '@mui/icons-material/Announcement';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import ExploreIcon from '@mui/icons-material/Explore';
import ExtensionIcon from '@mui/icons-material/Extension';
import HomeIcon from '@mui/icons-material/Home';

import {
  CatalogIcon,
  DocsIcon,
  GroupIcon,
  Link,
  Sidebar,
  SidebarDivider,
  SidebarGroup,
  SidebarItem,
  SidebarPage,
  SidebarSpace,
  sidebarConfig,
  useSidebarOpenState,
  SupportButton,
} from '@backstage/core-components';

import { MyGroupsSidebarItem } from '@backstage/plugin-org';
import { SidebarSearchModal } from '@backstage/plugin-search';
import {
  DefaultResultListItem,
  SearchContextProvider,
} from '@backstage/plugin-search-react';
import { TechDocsSearchResultListItem } from '@backstage/plugin-techdocs';
import { Settings as SidebarSettings } from '@backstage/plugin-user-settings';

import { NewAnnouncementBanner } from '@backstage-community/plugin-announcements';

import { CatalogSearchResultListItem } from '@internal/plugin-search-mdsol';

import LogoFull from './LogoFull';
import LogoIcon from './LogoIcon';

const useSidebarLogoStyles = makeStyles({
  root: {
    width: sidebarConfig.drawerWidthClosed,
    height: 3 * sidebarConfig.logoHeight,
    display: 'flex',
    flexFlow: 'row nowrap',
    alignItems: 'center',
    marginBottom: -14,
  },
  link: {
    width: sidebarConfig.drawerWidthClosed,
    marginLeft: 10,
  },
});

const SidebarLogo = () => {
  const classes = useSidebarLogoStyles();
  const { isOpen } = useSidebarOpenState();

  return (
    <div className={classes.root}>
      <Link to="/" underline="none" className={classes.link} aria-label="Home">
        {isOpen ? <LogoFull /> : <LogoIcon />}
      </Link>
    </div>
  );
};

export const Root = ({ children }: PropsWithChildren<{}>) => (
  <SidebarPage>
    <Sidebar>
      <SidebarLogo />
      <SearchContextProvider>
        <SidebarSearchModal
          resultItemComponents={[
            <CatalogSearchResultListItem icon={<CatalogIcon />} />,
            <TechDocsSearchResultListItem icon={<DocsIcon />} />,
            <DefaultResultListItem />,
          ]}
        />
      </SearchContextProvider>
      <SidebarDivider />
      <SidebarGroup label="Menu">
        <SidebarItem icon={HomeIcon} to="" text="Home" />
        <SidebarItem icon={CatalogIcon} to="catalog" text="Catalog" />
        <SidebarItem icon={ExtensionIcon} to="api-docs" text="APIs" />
        <SidebarItem icon={DocsIcon} to="docs" text="Docs" />
        <SidebarItem icon={ExploreIcon} to="explore" text="Explore" />
        <SidebarItem icon={EmojiEventsIcon} to="maturity" text="Maturity" />
        <SidebarItem
          icon={AccountTreeIcon}
          to="mcatalog-graph"
          text="Catalog Graph"
        />
      </SidebarGroup>
      <SidebarDivider />
      <MyGroupsSidebarItem
        singularTitle="My team"
        pluralTitle="My teams"
        icon={GroupIcon}
      />
      <SidebarSpace />
      <SidebarItem
        icon={AnnouncementIcon}
        to="announcements"
        text="Announcements"
      />
      <SidebarSettings />
      <SupportButton>Learn about Devops Central</SupportButton>
    </Sidebar>
    <NewAnnouncementBanner />
    {children}
  </SidebarPage>
);
